
import React from "react";
import { Link } from "react-router-dom";
import { usePermissions } from "@/context/PermissionsContext";
import { Resource } from "@/hooks/rollenbeheer/useRolePermissions";
import { UserRole } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/components/ui/sidebar";
import { 
  Home, 
  Database, 
  Bell, 
  BarChart3, 
  Users,
  ShieldCheck,
  MessageSquare,
  LayoutDashboard,
  Package,
  UserPlus,
  Workflow,
  BookOpen,
  AlertTriangle,
  KeyRound,
  PackagePlus,
  ShoppingCart,
  PackageCheck,
} from "lucide-react";
import { 
  SidebarMenu, 
  SidebarMenuItem, 
  SidebarMenuButton
} from "@/components/ui/sidebar";

// Menu item type definition
interface MenuItem {
  title: string;
  icon: React.ElementType;
  path: string;
  resource: Resource;
  permissionType?: "read" | "write" | "delete";
}

// List of all menu items with their paths for getting page titles
export const menuItems: MenuItem[] = [
  { title: "Dashboard", icon: Home, path: "/dashboard", resource: "dashboard" },
  { title: "Voorraad", icon: Database, path: "/inventory", resource: "inventory" },
  { title: "Meldingen", icon: Bell, path: "/notifications", resource: "notifications" },
  { title: "Feedback", icon: MessageSquare, path: "/feedback", resource: "feedback" },
  { title: "Pakket Ophaaltijden", icon: Package, path: "/package-pickup", resource: "packagePickup" },
  { title: "Rapportages", icon: BarChart3, path: "/reports", resource: "reports" },
  { title: "Medewerkers", icon: Users, path: "/medewerkers", resource: "medewerkers" },
  { title: "Rollenbeheer", icon: ShieldCheck, path: "/rollenbeheer", resource: "rollenbeheer" },
  { title: "Gebruikersbeheer", icon: KeyRound, path: "/admin/gebruikers", resource: "gebruikersbeheer" },
  { title: "Registratie Aanvragen", icon: UserPlus, path: "/admin/registraties", resource: "registraties" },
  { title: "Nieuws+", icon: LayoutDashboard, path: "/modal-campagnes", resource: "nieuws" },
  { title: "Workflow Executions", icon: Workflow, path: "/workflow-executions", resource: "workflowExecutions" },
  { title: "Vervallen Producten", icon: AlertTriangle, path: "/expired-products", resource: "expiredProducts" },
  { title: "Nieuwe Producten", icon: PackagePlus, path: "/nieuwe-producten", resource: "nieuweProducten" },
  { title: "Orders", icon: ShoppingCart, path: "/orders", resource: "orders" },
  { title: "Kleuren Orders", icon: PackageCheck, path: "/kleuren-orders", resource: "kleurenOrders" },
  { title: "API Documentatie", icon: BookOpen, path: "/api-docs", resource: "apiDocs" },
];

const MenuItems = ({ role }: { role: UserRole }) => {
  const { hasPermission } = usePermissions();
  const isMobile = useIsMobile();
  const { setCollapsed } = useSidebar();
  
  // Filter items based on permissions from rollenbeheer
  const allowedItems = menuItems.filter(item => {
    const hasAccess = hasPermission(item.resource, item.permissionType || "read");
    return hasAccess;
  });

  const handleClick = () => {
    if (isMobile) {
      setCollapsed(true);
    }
  };

  if (isMobile) {
    return (
      <div className="grid grid-cols-3 gap-1 px-2">
        {allowedItems.map((item) => (
          <Link
            key={item.title}
            to={item.path}
            onClick={handleClick}
            className="flex flex-col items-center justify-center gap-1 p-3 rounded-lg min-h-[56px] text-muted-foreground hover:bg-accent hover:text-accent-foreground active:bg-accent/80 transition-colors"
          >
            <item.icon className="w-5 h-5" />
            <span className="text-[11px] leading-tight text-center line-clamp-2">{item.title}</span>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <>
      {allowedItems.map((item) => (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton asChild>
            <Link to={item.path} className="flex items-center" onClick={handleClick}>
              <item.icon className="w-5 h-5 mr-3" />
              <span>{item.title}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </>
  );
};

export default MenuItems;

